import React, { useRef, useEffect, useMemo } from "react";
import { Sprite, Container } from "@pixi/react";
import { useButtonHover } from "../../../../Hooks/useButtonHover";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toggleGlobalSound, closeMenu } from "../../../../State/actions";
import { selectSceneTransitionActive } from "../../../../State/selectors";
import { SOUND_NAME } from "../../../../sounds";
import { getUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PRIMARY_USER_INTERFACE_BUTTON_CONFIG } from "../../../../pixiSettings";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";

// Position config — extracted for easy tweaking
const SOUND_BUTTON_POSITION_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => buttonConfig.width * 1.0,
      y: (toolbar) => toolbar.height / 2,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => buttonConfig.width * 1.0,
      y: (toolbar) => toolbar.height / 2,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => buttonConfig.width * 1.0,
      y: (toolbar) => toolbar.height / 2,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => buttonConfig.width * 1.0,
      y: (toolbar) => toolbar.height / 2,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    x: (toolbar, buttonConfig) => buttonConfig.width,
    y: (toolbar) => toolbar.height / 2,
  },
};

const ButtonSound = ({ toolbar, position: positionOverride }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { globalSoundDisabled } = useSelector((state) => state.sound);
  const sceneTransitionActive = useSelector(selectSceneTransitionActive);
  const { getResponsiveConfig, device, orientation } = useDimensions();

  const { hoverContainerRef, buttonRef, handlePointerOver, handlePointerOut, triggerPress } = useButtonHover();
  const isProcessingClickRef = useRef(false);
  const debounceTimeoutRef = useRef(null);

  const iconName = globalSoundDisabled
    ? "icon_sound_disable"
    : "icon_sound_enable";
  const texture = getUITexture(iconName);

  const buttonConfig = useMemo(
    () => getResponsiveConfig(PRIMARY_USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Position using config
  const buttonPosition = useMemo(() => {
    const deviceConfig = SOUND_BUTTON_POSITION_CONFIG[device];
    const posConfig = deviceConfig?.[orientation] ?? deviceConfig;
    return {
      x: posConfig.x(toolbar, buttonConfig),
      y: posConfig.y(toolbar, buttonConfig),
    };
  }, [device, orientation, toolbar, buttonConfig]);

  // Allow parent to override position
  const finalPosition = positionOverride || buttonPosition;

  const handleClick = () => {
    if (sceneTransitionActive) return;

    dispatch(closeMenu());


    if (isProcessingClickRef.current) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;

    soundManager.play(SOUND_NAME.button_click);

    triggerPress();

    dispatch(toggleGlobalSound());

    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);



  const isButtonDisabled = sceneTransitionActive;

  return (
    <Container
      name="secondary_backport_button_sound"
      x={finalPosition.x}
      y={finalPosition.y}
      zIndex={2}
      alpha={isButtonDisabled ? 0.5 : 1}
    >
      <Container ref={hoverContainerRef}>
        <Sprite
          ref={buttonRef}
          texture={texture}
          name="button_sound_icon"
          anchor={0.5}
          width={buttonConfig.width}
          height={buttonConfig.height}
          x={0}
          y={0}
          eventMode={isButtonDisabled ? "none" : "static"}
          cursor={isButtonDisabled ? "default" : "pointer"}
          onpointertap={handleClick}
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
          accessible={true}
          accessibleTitle={
            globalSoundDisabled ? t("enable_sound", { ns: "common" }) : t("disable_sound", { ns: "common" })
          }
          accessibleHint={
            globalSoundDisabled ? t("enable_sound", { ns: "common" }) : t("disable_sound", { ns: "common" })
          }
          resolution={2}
        />
      </Container>
    </Container>
  );
};

export default ButtonSound;
